import { i18n } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import type { MessageReaction, ThreadMessage } from "@rakazo/contracts";
import type { FoldedTranscript, ThreadItem } from "@rakazo/core";
import {
  expandPeerReplies,
  groupPeerReceipts,
  groupVoiceChats,
  isPurePeerReceipt,
  isToolActivityBlock,
  projectMessageReactions,
  timeSeparatorIds,
  unreadDividerMessageId,
  userVisibleMessages,
  voiceChatHoldsMessage,
} from "@rakazo/core";
import type { GroupAvatarMember } from "@rakazo/ui-web";
import { cn } from "@rakazo/ui-web";
import { ChevronDown, LoaderCircle, MessagesSquare } from "lucide-react";
import type { ReactNode, RefObject } from "react";
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ActiveBotGlyph } from "../../components/ai/CollaborationMarker";
import { VoiceChatCard } from "../../components/call/VoiceChatCard";
import { JumpToLatestButton } from "../../components/JumpToLatestButton";
import type { ArtifactTarget } from "../../lib/artifact-open";
import { applyChatTextSize, getChatTextSize } from "../../lib/chat-text-size-pref";
import { quoteDraftForSelection } from "../../lib/quote-selection";
import { messageHasVisibleBlocks } from "../../lib/tool-activity-view";
import { transcriptIsNearEnd, transcriptMovedDown } from "../../lib/transcript-scroll";
import { useStableValue } from "../../lib/use-stable-value";
import { MessageTime, TimeSeparator, UnreadDivider } from "./chat-context";
import { MessageHoverActions } from "./message-hover-actions";
import { MessageView } from "./message-view";
import type { OutgoingMessage } from "./outgoing-message";
import { OutgoingMessageView } from "./outgoing-message";
import { QuoteSelectionButton } from "./quote-selection-button";

const NO_OUTGOING_MESSAGES: OutgoingMessage[] = [];

export const Transcript = memo(function Transcript({
  emptyState,
  loading = false,
  scrollRef,
  scrollRequest,
  followLatestRequest,
  onScrollRequestHandled,
  artifactTarget,
  transcript,
  showToolActivity,
  olderCursor,
  unreadAfterSeq,
  initialReadReady = true,
  loadingOlder,
  answerableAskIds,
  dismissableAskIds,
  running,
  workingBots,
  executionWait,
  onCallBack,
  onLoadOlder,
  onOpenBot,
  onAnswer,
  onReply,
  onQuote,
  onReact,
  onJumpToMessage,
  onOpenPeerMessages,
  memberName,
  peerBot,
  onRefresh,
  onJumpToLatest,
  onBotChanged,
  onAddRoutine,
  voiceReady,
  speakingMessageId,
  onSpeak,
  onShareRoom,
  onOpenComputer,
  outgoingMessages = NO_OUTGOING_MESSAGES,
  sending = false,
}: {
  emptyState?: ReactNode;
  loading?: boolean;
  scrollRef: RefObject<HTMLDivElement | null>;
  scrollRequest: { messageId: string; nonce: number } | null;
  followLatestRequest?: string;
  onScrollRequestHandled: () => void;
  artifactTarget: ArtifactTarget;
  transcript: FoldedTranscript<ThreadMessage>;
  showToolActivity: boolean;
  olderCursor: number | null;
  /** The seq read up to when this chat opened; null when nothing was unread. */
  unreadAfterSeq: number | null;
  initialReadReady?: boolean;
  loadingOlder: boolean;
  answerableAskIds: Set<string>;
  dismissableAskIds: Set<string>;
  running: boolean;
  executionWait?: string | null;
  workingBots: GroupAvatarMember[];
  /** Calls the bot back from a missed call in the transcript. */
  onCallBack?: () => void;
  onLoadOlder: () => void | Promise<void>;
  onOpenBot: (botId: string) => void;
  onAnswer: (
    message: ThreadMessage,
    text: string,
    username?: string,
    dismiss?: boolean,
    files?: File[],
  ) => Promise<void>;
  onReply: (message: ThreadMessage) => void;
  onQuote: (message: ThreadMessage, quote: string) => void;
  onReact: (message: ThreadMessage, reaction: MessageReaction) => Promise<void>;
  onJumpToMessage: (messageId: string) => void;
  onOpenPeerMessages: (peer: { peerBotId: string; peerBotName: string }) => void;
  memberName?: (botId: string | undefined) => string | undefined;
  peerBot: (botId: string) => { name?: string; color: string; status?: string } | undefined;
  onRefresh: () => Promise<void>;
  /** Present while the historical window omits the latest page.
   * Наявний, поки історичне вікно не містить найновішої сторінки. */
  onJumpToLatest?: () => Promise<void>;
  onBotChanged: () => Promise<void>;
  onAddRoutine: (name: string, prompt: string) => void;
  voiceReady: boolean;
  speakingMessageId: string | null;
  onSpeak: (message: ThreadMessage) => void;
  onShareRoom?: (message: ThreadMessage) => void;
  onOpenComputer: (botId?: string) => void;
  outgoingMessages?: OutgoingMessage[];
  sending?: boolean;
}) {
  const { t } = useLingui();
  const [atEnd, setAtEnd] = useState(true);
  // The setting lives on <html> so the CSS can size the transcript and the
  // composer together without threading a prop through the shell.
  useLayoutEffect(() => {
    applyChatTextSize(getChatTextSize());
  }, []);
  const following = useRef(true);
  const entryPositioned = useRef(false);
  const autoScrolling = useRef(false);
  const lastScrollTop = useRef<number | null>(null);
  const autoScrollTimer = useRef<number | undefined>(undefined);
  const jumpButtonRef = useRef<HTMLButtonElement>(null);
  const [expandedPeerMarkers, setExpandedPeerMarkers] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const togglePeerReplies = useCallback((markerId: string) => {
    // Like a pointer press: a keyboard toggle must not snap the transcript to its end.
    following.current = false;
    autoScrolling.current = false;
    setExpandedPeerMarkers((expanded) => {
      const next = new Set(expanded);
      if (!next.delete(markerId)) next.add(markerId);
      return next;
    });
  }, []);
  // A jump to a folded reply expands its marker; the scroll effect below then finds the row.
  useLayoutEffect(() => {
    const markerId = scrollRequest && transcript.markerOf.get(scrollRequest.messageId);
    if (!markerId) return;
    setExpandedPeerMarkers((expanded) =>
      expanded.has(markerId) ? expanded : new Set(expanded).add(markerId),
    );
  }, [scrollRequest, transcript]);
  const messages = useMemo(
    () => expandPeerReplies(transcript, expandedPeerMarkers),
    [transcript, expandedPeerMarkers],
  );
  // Expanded history is inspectable, but never becomes a new human-facing event.
  // Історія доступна для перегляду, але не стає новою подією для людини.
  const humanMessages = useStableValue(useMemo(() => userVisibleMessages(messages), [messages]));
  const messageById = useMemo(
    () => new Map(messages.map((message) => [message.id, message])),
    [messages],
  );
  const reactionView = useMemo(() => projectMessageReactions(messages), [messages]);
  // Memoised: a fresh callback from the shell must not redo the O(n) passes or re-fire the
  // layout effects keyed on these lists.
  // Мемоізовано: нова функція від Shell не має перераховувати списки й перезапускати ефекти.
  const shownMessages = useMemo(
    () =>
      reactionView.visibleMessages.filter((message) =>
        messageHasVisibleBlocks(message.blocks, showToolActivity),
      ),
    [reactionView, showToolActivity],
  );
  const shownMessageIds = useMemo(
    () => new Set(shownMessages.map((message) => message.id)),
    [shownMessages],
  );
  // Group the original receipts before inserting children, so the group can always collapse.
  // Спершу групуємо початкові маркери: вставлені відповіді не прибирають кнопку згортання.
  const receiptBaseMessages = useMemo(
    () => shownMessages.filter((message) => !transcript.markerOf.has(message.id)),
    [shownMessages, transcript.markerOf],
  );
  const voiceEntries = useMemo(() => groupVoiceChats(receiptBaseMessages), [receiptBaseMessages]);
  const separatorIds = useMemo(() => timeSeparatorIds(shownMessages), [shownMessages]);
  const humanShownMessages = useMemo(
    () =>
      humanMessages.filter((message) => messageHasVisibleBlocks(message.blocks, showToolActivity)),
    [humanMessages, showToolActivity],
  );
  const unreadDividerId = unreadDividerMessageId(humanShownMessages, unreadAfterSeq, olderCursor);
  const receiptEntries = useMemo(
    () => groupPeerReceipts(voiceEntries, new Set(unreadDividerId ? [unreadDividerId] : [])),
    [voiceEntries, unreadDividerId],
  );
  const [expandedReceiptIds, setExpandedReceiptIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  useLayoutEffect(() => {
    const id = scrollRequest?.messageId;
    if (!id) return;
    const markerId = transcript.markerOf.get(id);
    setExpandedReceiptIds((current) => {
      if (current.has(id) && (!markerId || current.has(markerId))) return current;
      const next = new Set(current).add(id);
      if (markerId) next.add(markerId);
      return next;
    });
  }, [scrollRequest, transcript.markerOf]);

  const workingBotName = workingBots.length === 1 ? workingBots[0]?.name : undefined;
  const workingLabel =
    workingBotName != null && workingBotName !== ""
      ? t`${workingBotName} is working`
      : t`Bots are working`;
  const [quoteDraft, setQuoteDraft] = useState<{
    message: ThreadMessage;
    text: string;
    range: Range;
  } | null>(null);
  const selectingWithMouse = useRef(false);

  const evaluateSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      setQuoteDraft(null);
      return;
    }
    const range = selection.getRangeAt(0);
    const contentOf = (node: Node) =>
      (node instanceof Element ? node : node.parentElement)?.closest<HTMLElement>(
        "[data-quote-message-id]",
      ) ?? null;
    const startContent = contentOf(range.startContainer);
    const endContent = contentOf(range.endContainer);
    // selection.toString() serializes the whole range (a Ctrl+A transcript is
    // unbounded), so it only runs once both endpoints sit in one message.
    const draft = quoteDraftForSelection(
      {
        startContent,
        endContent,
        text: startContent && startContent === endContent ? selection.toString() : "",
      },
      messageById,
    );
    setQuoteDraft((prev) => {
      if (!draft) return null;
      // Repeat firings for an unchanged selection reuse the draft so the
      // transcript isn't re-rendered by every unrelated selection event. A
      // moved selection (e.g. keyboard-selecting a second occurrence of the
      // same text) must carry its new Range — the pill anchors to it.
      if (
        prev &&
        prev.message === draft.message &&
        prev.text === draft.text &&
        prev.range.compareBoundaryPoints(Range.START_TO_START, range) === 0 &&
        prev.range.compareBoundaryPoints(Range.END_TO_END, range) === 0
      ) {
        return prev;
      }
      return { ...draft, range };
    });
  }, [messageById]);

  // Keyboard and assistive-tech selections never reach a mouseup, so the pill
  // lifecycle listens on selectionchange; the mouse flag keeps it hidden while
  // a drag is still in flight.
  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      selectingWithMouse.current = true;
      if ((event.target as Element | null)?.closest?.("[data-quote-selection]")) return;
      setQuoteDraft(null);
    };
    const onMouseUp = () => {
      selectingWithMouse.current = false;
      evaluateSelection();
    };
    const onSelectionChange = () => {
      if (!selectingWithMouse.current) evaluateSelection();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof Node && scrollRef.current?.contains(event.target)) {
        entryPositioned.current = true;
      }
      if (event.key === "Escape") setQuoteDraft(null);
    };
    // A drag that ends outside the window never fires document mouseup; reset
    // the flag on blur so keyboard selections keep working afterwards.
    const onWindowBlur = () => {
      selectingWithMouse.current = false;
    };
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("mouseup", onMouseUp, true);
    document.addEventListener("selectionchange", onSelectionChange);
    document.addEventListener("keydown", onKey);
    window.addEventListener("blur", onWindowBlur);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("mouseup", onMouseUp, true);
      document.removeEventListener("selectionchange", onSelectionChange);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [evaluateSelection, scrollRef]);
  const snapToEnd = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    following.current = true;
    autoScrolling.current = false;
    setAtEnd(true);
    element.scrollTo({ top: element.scrollHeight, behavior: "auto" });
  }, [scrollRef]);

  const jumpToLatest = useCallback(async () => {
    const element = scrollRef.current;
    if (!element) return;
    if (onJumpToLatest) {
      try {
        await onJumpToLatest();
      } catch {
        return;
      }
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      if (scrollRef.current !== element) return;
    }
    // An explicit jump wins over a delayed entry boundary.
    // Явний перехід має пріоритет над затриманою межею непрочитаного.
    entryPositioned.current = true;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    following.current = true;
    autoScrolling.current = !reducedMotion;
    setAtEnd(true);
    element.scrollTo({
      top: element.scrollHeight,
      behavior: reducedMotion ? "auto" : "smooth",
    });
    window.clearTimeout(autoScrollTimer.current);
    // Fallback only: onScroll clears autoScrolling once near-end is reached.
    autoScrollTimer.current = window.setTimeout(
      () => {
        autoScrolling.current = false;
      },
      reducedMotion ? 0 : 2_000,
    );
  }, [onJumpToLatest, scrollRef]);

  const scrolledJump = useRef<number | null>(null);
  const jumpScrolling = useRef(false);
  const jumpScrollTimer = useRef<number | undefined>(undefined);
  const endJumpScroll = useCallback(() => {
    jumpScrolling.current = false;
    window.clearTimeout(jumpScrollTimer.current);
    scrollRef.current?.removeEventListener("scrollend", endJumpScroll);
  }, [scrollRef]);
  useLayoutEffect(() => {
    if (!followLatestRequest) return;
    entryPositioned.current = true;
    // A send explicitly resumes following, including the message that arrives over SSE later.
    // Надсилання явно поновлює прокрутку, зокрема для повідомлення, яке надійде пізніше.
    endJumpScroll();
    snapToEnd();
  }, [followLatestRequest, endJumpScroll, snapToEnd]);
  // A jump scroll must win over follow-the-tail: unfollow inside the commit
  // that mounts the row so a live commit cannot cancel the animation, keep
  // retrying while the pinned window is still rendering, and suppress the
  // near-end follow re-arm only for the jump's own scroll events — scrollend
  // (or user input interrupting it, which also fires scrollend) ends the
  // suppression, with the timeout as fallback when no scroll happens.
  useLayoutEffect(() => {
    if (!scrollRequest || scrolledJump.current === scrollRequest.nonce) return;
    entryPositioned.current = true;
    const element = scrollRef.current;
    const row = element?.querySelector(
      `[data-message-id="${CSS.escape(scrollRequest.messageId)}"]`,
    );
    if (!element || !row) return;
    scrolledJump.current = scrollRequest.nonce;
    following.current = false;
    autoScrolling.current = false;
    jumpScrolling.current = true;
    element.addEventListener("scrollend", endJumpScroll, { once: true });
    window.clearTimeout(jumpScrollTimer.current);
    jumpScrollTimer.current = window.setTimeout(endJumpScroll, 2_000);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Streaming layout updates can interrupt a smooth jump before it reaches the row.
    row.scrollIntoView({ behavior: "auto", block: "center" });
    row.animate(
      reducedMotion
        ? [{ backgroundColor: "var(--muted)" }, { backgroundColor: "var(--muted)" }]
        : [
            { backgroundColor: "transparent" },
            { backgroundColor: "var(--muted)" },
            { backgroundColor: "transparent" },
          ],
      { duration: 1200 },
    );
    onScrollRequestHandled();
  }, [
    messages,
    expandedReceiptIds,
    scrollRequest,
    scrollRef,
    endJumpScroll,
    onScrollRequestHandled,
  ]);

  useLayoutEffect(() => {
    if (entryPositioned.current || !initialReadReady || !messages.length) return;
    if (scrollRequest || followLatestRequest) {
      entryPositioned.current = true;
      return;
    }
    const latestUnread =
      unreadAfterSeq === null
        ? undefined
        : humanShownMessages.findLast(
            (message) =>
              message.role === "bot" && message.seq !== undefined && message.seq > unreadAfterSeq,
          );
    if (!latestUnread) {
      entryPositioned.current = true;
      return;
    }
    const element = scrollRef.current;
    const voice = voiceEntries.find(
      (entry) =>
        entry.kind === "voiceChat" &&
        entry.messages.some((message) => message.id === latestUnread.id),
    );
    const receiptGroup = receiptEntries.find(
      (entry) =>
        entry.kind === "peerReceipts" &&
        entry.messages.some((message) => message.id === latestUnread.id),
    );
    const selector =
      voice?.kind === "voiceChat"
        ? `[data-voice-chat-key="${CSS.escape(voice.key)}"]`
        : receiptGroup?.kind === "peerReceipts"
          ? `[data-peer-group="${CSS.escape(receiptGroup.key)}"]`
          : `[data-message-id="${CSS.escape(latestUnread.id)}"]`;
    const row = element?.querySelector<HTMLElement>(selector);
    if (!element || !row) return;
    entryPositioned.current = true;
    following.current = false;
    autoScrolling.current = false;
    // Enter at the start of the newest unread reply; long replies must not open at their end.
    // Відкриваємо початок найновішої непрочитаної відповіді, а не кінець довгого тексту.
    // Scroll only the transcript; scrollIntoView can also shift its surrounding panels.
    // Прокручуємо тільки чат: scrollIntoView може також зсунути зовнішні панелі.
    const padding = Number.parseFloat(getComputedStyle(element).scrollPaddingTop) || 0;
    element.scrollTo({
      top:
        element.scrollTop +
        row.getBoundingClientRect().top -
        element.getBoundingClientRect().top -
        padding,
      behavior: "auto",
    });
    const nearEnd = transcriptIsNearEnd(element);
    following.current = nearEnd;
    setAtEnd(nearEnd);
  }, [
    initialReadReady,
    unreadAfterSeq,
    humanShownMessages,
    voiceEntries,
    receiptEntries,
    messages.length,
    scrollRef,
    scrollRequest,
    followLatestRequest,
  ]);

  useLayoutEffect(() => {
    if (following.current) snapToEnd();
  }, [humanMessages, running, snapToEnd, outgoingMessages]);

  useLayoutEffect(() => {
    const button = jumpButtonRef.current;
    if (atEnd && button && document.activeElement === button) {
      button.blur();
    }
  }, [atEnd]);

  const loadOlder = useCallback(() => {
    const wasFollowing = following.current;
    // Prepend must not race the messages-driven snap-to-end follow path.
    following.current = false;
    autoScrolling.current = false;
    const pending = onLoadOlder();
    if (!pending) return;
    // The owner reports the failure (toast with Retry); the button's `void` call must not
    // leave an unhandled rejection behind. / Власник показує помилку; тут її не пробрасуємо.
    return Promise.resolve(pending).then(
      () => true,
      () => {
        const element = scrollRef.current;
        if (wasFollowing && element && transcriptIsNearEnd(element)) {
          following.current = true;
          setAtEnd(true);
        }
        return false;
      },
    );
  }, [onLoadOlder, scrollRef]);

  // A page of only hidden bot-to-bot history leaves nothing to show: reach further back. A failed
  // load waits for the button instead of retrying in a loop; a load the owner dropped (the chat
  // reopened meanwhile) may run again.
  // Сторінка лише з прихованим обміном ботів: завантажуємо далі; після помилки чекаємо кнопку.
  const autoLoadedCursor = useRef<number | null>(null);
  useEffect(() => {
    if (loading || loadingOlder || olderCursor == null || messages.length > 0) return;
    if (autoLoadedCursor.current === olderCursor) return;
    autoLoadedCursor.current = olderCursor;
    void loadOlder()?.then((loaded) => {
      if (loaded && autoLoadedCursor.current === olderCursor) autoLoadedCursor.current = null;
    });
  }, [loading, loadingOlder, olderCursor, messages.length, loadOlder]);

  useEffect(
    () => () => {
      window.clearTimeout(autoScrollTimer.current);
      window.clearTimeout(jumpScrollTimer.current);
    },
    [],
  );

  return (
    <div className="relative flex min-h-0 flex-1">
      <div
        ref={scrollRef}
        data-testid="transcript"
        data-loading={loading}
        aria-busy={loading || undefined}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label={t`Messages`}
        onPointerDown={(event) => {
          entryPositioned.current = true;
          lastScrollTop.current = event.currentTarget.scrollTop;
          autoScrolling.current = false;
          following.current = false;
        }}
        onTouchStart={(event) => {
          entryPositioned.current = true;
          lastScrollTop.current = event.currentTarget.scrollTop;
          autoScrolling.current = false;
          following.current = false;
        }}
        onWheel={(event) => {
          entryPositioned.current = true;
          if (event.deltaY < 0) {
            lastScrollTop.current = event.currentTarget.scrollTop;
            autoScrolling.current = false;
            following.current = false;
          }
        }}
        onScroll={(event) => {
          const scrolledDown = transcriptMovedDown(
            lastScrollTop.current,
            event.currentTarget.scrollTop,
          );
          lastScrollTop.current = event.currentTarget.scrollTop;
          const nearEnd = transcriptIsNearEnd(event.currentTarget);
          setAtEnd(nearEnd);
          // A jump scroll owns the viewport until its animation settles; its
          // own near-end crossings must not re-arm tail-following.
          if (jumpScrolling.current) return;
          if (nearEnd) {
            if (scrolledDown) following.current = true;
            if (autoScrolling.current) {
              autoScrolling.current = false;
              window.clearTimeout(autoScrollTimer.current);
            }
          } else if (!autoScrolling.current) {
            following.current = false;
          }
        }}
        className="rk-scroll flex min-h-0 flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto px-4 py-5 md:px-7 md:py-6"
      >
        {messages.length === 0 && outgoingMessages.length === 0 && !running && olderCursor == null
          ? emptyState
          : null}
        {olderCursor != null ? (
          <button
            type="button"
            disabled={loadingOlder}
            onClick={() => void loadOlder()}
            className="self-center rounded-lg px-3 py-1.5 text-[13px] text-muted-foreground hover:bg-muted hover:text-foreground/75 disabled:opacity-50"
          >
            <span className="inline-flex items-center gap-2">
              {loadingOlder ? (
                <LoaderCircle
                  aria-hidden="true"
                  size={14}
                  className="animate-spin motion-reduce:animate-none"
                />
              ) : null}
              {loadingOlder ? t`Loading…` : t`Load earlier messages`}
            </span>
          </button>
        ) : null}
        {receiptEntries.map((entry) => {
          const renderItem = (
            item: ThreadItem<ThreadMessage>,
            showPeerContent = false,
          ): ReactNode => {
            if (item.kind === "voiceChat") {
              return (
                <div key={item.key} data-voice-chat-key={item.key}>
                  {item.messages[0] && separatorIds.has(item.messages[0].id) ? (
                    <TimeSeparator
                      createdAt={item.messages[0].createdAt}
                      locale={i18n.locale || "en"}
                    />
                  ) : null}
                  {voiceChatHoldsMessage(item, unreadDividerId) ? (
                    <UnreadDivider
                      afterSeparator={Boolean(
                        item.messages[0] && separatorIds.has(item.messages[0].id),
                      )}
                    />
                  ) : null}
                  <VoiceChatCard
                    group={item}
                    revealMessageId={scrollRequest?.messageId}
                    onCallBack={onCallBack}
                  />
                  {item.messages[0] ? (
                    <div className="mt-1 flex justify-start">
                      <MessageTime
                        createdAt={item.messages[0].createdAt}
                        locale={i18n.locale || "en"}
                      />
                    </div>
                  ) : null}
                </div>
              );
            }
            const message = item.message;
            if (!messageHasVisibleBlocks(message.blocks, showToolActivity)) return null;
            const peerReceipt = isPurePeerReceipt(message);
            const messageReactions = reactionView.reactions.get(message.id);
            return (
              <Fragment key={message.id}>
                {separatorIds.has(message.id) ? (
                  <TimeSeparator createdAt={message.createdAt} locale={i18n.locale || "en"} />
                ) : null}
                {message.id === unreadDividerId ? (
                  <UnreadDivider afterSeparator={separatorIds.has(message.id)} />
                ) : null}
                <div
                  data-message-id={message.id}
                  data-peer-receipt={peerReceipt ? "true" : undefined}
                  aria-live={message.id.startsWith("progress:") ? "off" : undefined}
                  className={peerReceipt ? "relative" : "group/message relative hover:z-20"}
                >
                  <div
                    className={
                      peerReceipt
                        ? undefined
                        : `relative flex ${message.role === "user" ? "justify-end" : "justify-start"}`
                    }
                  >
                    <div
                      data-testid={peerReceipt ? undefined : "message-bubble-frame"}
                      data-message-role={message.role}
                      className={
                        peerReceipt
                          ? undefined
                          : message.blocks.some(
                                (block) => block.kind === "ask" || block.kind === "mcp_approval",
                              )
                            ? "message-bubble-frame relative w-full min-w-0 max-w-full"
                            : `message-bubble-frame relative ${message.blocks.some((block) => block.kind === "app_connect") ? "w-full" : "w-fit"} min-w-0 ${
                                message.role === "user" ? "max-w-[84%]" : "max-w-[88%]"
                              }`
                      }
                    >
                      <div className={peerReceipt ? undefined : "message-content"}>
                        <MessageView
                          artifactTarget={artifactTarget}
                          message={message}
                          canAnswer={answerableAskIds.has(message.id)}
                          canDismiss={dismissableAskIds.has(message.id)}
                          onOpenBot={onOpenBot}
                          onOpenPeerMessages={onOpenPeerMessages}
                          peerReplies={
                            transcript.folded.has(message.id)
                              ? expandedPeerMarkers.has(message.id)
                                ? "expanded"
                                : "collapsed"
                              : undefined
                          }
                          onTogglePeerReplies={togglePeerReplies}
                          onAnswer={onAnswer}
                          speakerName={
                            peerReceipt
                              ? undefined
                              : message.role === "bot"
                                ? memberName?.(message.botId)
                                : undefined
                          }
                          memberName={memberName}
                          peerBot={peerBot}
                          replyParent={
                            message.replyToMessageId
                              ? messageById.get(message.replyToMessageId)
                              : undefined
                          }
                          onJumpToMessage={onJumpToMessage}
                          onRefresh={onRefresh}
                          onBotChanged={onBotChanged}
                          onAddRoutine={onAddRoutine}
                          voiceReady={voiceReady}
                          speaking={speakingMessageId === message.id}
                          onSpeak={onSpeak}
                          onOpenComputer={onOpenComputer}
                          showToolActivity={showToolActivity}
                          showPeerContent={
                            peerReceipt && (showPeerContent || expandedPeerMarkers.has(message.id))
                          }
                        />
                      </div>
                      {!peerReceipt && messageReactions ? (
                        <div
                          data-testid="message-reactions"
                          className={cn(
                            "mt-1 flex flex-wrap gap-1",
                            message.role === "user" && "justify-end",
                          )}
                        >
                          {[...messageReactions].map(([emoji, count]) => (
                            <span
                              key={emoji}
                              className="rounded-full border border-border bg-muted px-2 py-0.5 text-xs"
                            >
                              {emoji}
                              {count > 1 ? ` ${count}` : ""}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      {!peerReceipt && !message.id.startsWith("progress:") ? (
                        <MessageHoverActions
                          message={message}
                          side={message.role === "user" ? "start" : "end"}
                          onReply={onReply}
                          onReact={onReact}
                          onShare={onShareRoom}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
                {expandedPeerMarkers.has(message.id)
                  ? (transcript.folded.get(message.id) ?? [])
                      .filter((reply) => shownMessageIds.has(reply.id))
                      .map((reply) => renderItem({ kind: "message", message: reply }, true))
                  : null}
              </Fragment>
            );
          };
          if (entry.kind !== "peerReceipts") return renderItem(entry);
          const count = entry.messages.length;
          const expanded = entry.messages.some((message) => expandedReceiptIds.has(message.id));
          const first = entry.messages[0]!;
          return (
            <div
              key={entry.key}
              data-peer-group={entry.key}
              className="mx-auto w-full max-w-[860px] md:max-w-none"
            >
              {!expanded && separatorIds.has(first.id) ? (
                <TimeSeparator createdAt={first.createdAt} locale={i18n.locale || "en"} />
              ) : null}
              {!expanded && first.id === unreadDividerId ? (
                <UnreadDivider afterSeparator={separatorIds.has(first.id)} />
              ) : null}
              <button
                type="button"
                aria-expanded={expanded}
                data-testid="peer-receipts-toggle"
                className="flex min-h-11 max-w-full items-center gap-2 rounded-xl px-2.5 text-sm text-muted-foreground hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => {
                  following.current = false;
                  autoScrolling.current = false;
                  setExpandedReceiptIds((current) => {
                    const next = new Set(current);
                    for (const message of entry.messages) {
                      if (expanded) next.delete(message.id);
                      else next.add(message.id);
                    }
                    return next;
                  });
                }}
              >
                <MessagesSquare size={15} aria-hidden="true" />
                <span>
                  {t`Messages between bots`} · <span className="tabular-nums">{count}</span>
                </span>
                <ChevronDown
                  size={14}
                  className={expanded ? "" : "-rotate-90"}
                  aria-hidden="true"
                />
              </button>
              {expanded ? (
                <div className="flex flex-col gap-0">
                  {entry.messages.map((message) => renderItem({ kind: "message", message }, true))}
                </div>
              ) : null}
            </div>
          );
        })}
        {outgoingMessages.map((message) => (
          <OutgoingMessageView key={message.nonce} message={message} retryDisabled={sending} />
        ))}
        {executionWait ? (
          <p role="status" className="px-1 text-sm text-muted-foreground">
            {executionWait}
          </p>
        ) : running &&
          !messages.some(
            (message) =>
              message.id.startsWith("progress:") &&
              message.blocks.some(
                (block) =>
                  block.kind === "progress" && !isToolActivityBlock(block) && Boolean(block.text),
              ),
          ) ? (
          <ActiveBotGlyph bots={workingBots} label={workingLabel} />
        ) : null}
      </div>
      {quoteDraft ? (
        <QuoteSelectionButton
          range={quoteDraft.range}
          onQuote={() => {
            onQuote(quoteDraft.message, quoteDraft.text);
            window.getSelection()?.removeAllRanges();
            setQuoteDraft(null);
          }}
        />
      ) : null}
      <JumpToLatestButton
        ref={jumpButtonRef}
        atEnd={atEnd && !onJumpToLatest}
        onClick={jumpToLatest}
      />
    </div>
  );
});

/**
 * Floating Quote action anchored to the selection's bounding rect. Measures
 * itself after mount so it can flip below the selection when there is no room
 * above and stay clamped inside the viewport; re-anchors on scroll/resize.
 */
